import {
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import axios from "axios";
import { format } from "date-fns";
import { useSession } from "next-auth/react";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowLeft, ChevronRight, Copy, Heart } from "react-feather";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import useSWR from "swr";
import { useRouter } from "next/router";
import ReactPlayer from "react-player";
import { faHeart } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";

import Buttons from "@/components/Product/Buttons";
import EndProductInfo from "@/components/Product/EndProductInfo";
import GarageInfo from "@/components/Product/GarageInfo";
import ProductCard from "@/components/Product/ProductCard";
import ButtonAnimated from "@/components/common/ButtonAnimated";
import EndOrRefreshDialog, {
  DialogType,
} from "@/components/common/EndOrRefreshDialog";
import ErrorMessage from "@/components/common/ErrorMessage";
import FullPhotosModal from "@/components/common/FullPhotosModal";
import Image from "@/components/common/Image";
import Loader from "@/components/common/Loader";
import ProductsSlider from "@/components/common/ProductsSlider";
import useCurrentLocation from "@/hooks/useCurrentLocation";
import { useToastPromise } from "@/hooks/useToast";
import {
  getCategoryParents,
  checkIfCategoryIsService,
  checkIfCategoryIsRealEstate,
  checkIfCategoryIsAutomotive,
} from "@/utils/category";
import { formatPrice } from "@/utils/helpers";
import {
  getProductAdvertiserValue,
  onLikeOrDislikeProduct,
} from "@/utils/product";
import { mainSliderSettings } from "@/utils/helpers";
import CustomHead from "@/components/common/CustomHead";

const GARAGE_PRODUCTS_LIMIT = 3;

type Props = {
  slug: string | string[];
};

const Product = ({ slug }: Props): React.ReactElement => {
  const router = useRouter();
  const { t } = useTranslation();
  const { data: session, status } = useSession();
  const { location, error: locationError } = useCurrentLocation();
  const {
    data,
    error: dataError,
    mutate: refreshData,
  } = useSWR(
    location
      ? `/product/slug?slug=${slug}&coordinates[]=${location?.lat}&coordinates[]=${location?.lng}`
      : `/product/slug?slug=${slug}`
  );
  const { data: garageProductsData } = useSWR(
    data?.garage?.id
      ? `/product/garage/${data?.garage?.id}?limit=${GARAGE_PRODUCTS_LIMIT}&productId=${data?.id}` +
          `${
            location
              ? `&coordinates[]=${location?.lat}&coordinates[]=${location?.lng}`
              : ""
          }`
      : null
  );
  const { data: similarProductsData } = useSWR(
    status === "authenticated"
      ? `/product?coordinates[]=${location?.lat}&coordinates[]=${location?.lng}&userId=${session?.user?.id}&categories[]=${data?.categories[0]?.id}`
      : `/product?coordinates[]=${location?.lat}&coordinates[]=${location?.lng}&categories[]=${data?.categories[0]?.id}`
  );
  const productIsService = useMemo(
    () => checkIfCategoryIsService(data?.categories?.[0]),
    [data]
  );
  const productIsRealEstate = useMemo(
    () => checkIfCategoryIsRealEstate(data?.categories?.[0]),
    [data]
  );
  const productIsAutomotive = useMemo(
    () => checkIfCategoryIsAutomotive(data?.categories?.[0]),
    [data]
  );
  const productAdvertiserValue = useMemo(
    () =>
      productIsRealEstate || productIsAutomotive
        ? getProductAdvertiserValue(data)
        : null,
    [data, productIsAutomotive, productIsRealEstate]
  );

  const similarProducts = useMemo(
    () =>
      similarProductsData?.data?.filter((product) => product?.id !== data?.id),
    [data, similarProductsData]
  );

  const categoryParents = useMemo(
    () => getCategoryParents(data?.categories?.[0]),
    [data]
  );
  const categoryParametersValues = useMemo(() => {
    if (data?.categoryParameterValues?.length > 0) {
      const parametersValuesSorted = data?.categoryParameterValues
        ?.sort((a, b) => b?.parameterId - a?.parameterId)
        ?.filter((parameterValue) => parameterValue?.value !== null)
        ?.filter((parameterValue) => {
          if (
            parameterValue?.parameter?.type === "checkbox" &&
            parameterValue?.value === "false"
          )
            return false;
          else return true;
        });

      return parametersValuesSorted;
    } else return [];
  }, [data]);
  const [showVin, setShowVin] = useState(false);

  const formatter = new Intl.NumberFormat("pl-PL");

  const fullPhotosModal = useDisclosure();
  const [initialSlide, setInitialSlide] = useState(0);
  const [dialogVariant, setDialogVariant] = useState<DialogType>();
  const endOrRefreshDialog = useDisclosure();
  const toastPromise = useToastPromise();
  const isUserProduct =
    session?.user?.id && data?.user?.id && session?.user?.id === data?.user?.id;

  const onCloseDialog = () => {
    setDialogVariant(null);
    endOrRefreshDialog.onClose();
  };

  const onConfirmDialog = async () => {
    if (dialogVariant === DialogType.End)
      await toastPromise.promise(axios.put(`/product/${data?.id}/end`), {
        success: t("endProductSuccess"),
      });

    if (dialogVariant === DialogType.Refresh)
      await toastPromise.promise(axios.put(`/product/${data?.id}/refresh`), {
        success: t("refreshProductSuccess"),
      });

    refreshData();
    endOrRefreshDialog.onClose();
  };

  const [selectedLanguage, setSelectedLanguage] = useState<{
    label: string;
    value: string;
  }>();
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translatedDescription, setTranslatedDescription] = useState("");

  useEffect(() => {
    setTranslatedDescription("");
    setSelectedLanguage(null);
  }, [data]);

  const translateText = useCallback(() => {
    setTranslationLoading(true);

    axios
      .post(`/translate`, {
        text: data?.description,
        language: selectedLanguage?.value,
      })
      .then(({ data }) => setTranslatedDescription(data?.text))
      .catch(() => {})
      .finally(() => setTranslationLoading(false));
  }, [data?.description, selectedLanguage?.value]);

  useEffect(() => {
    if (router.locale) {
      const { locale } = router;

      if (locale === "uk")
        setSelectedLanguage({ label: "Ukrainian", value: "uk" });

      if (locale === "en")
        setSelectedLanguage({ label: "English (American)", value: "en-US" });
    }
  }, [router]);

  useEffect(() => {
    if (selectedLanguage?.value && data?.description) translateText();
  }, [selectedLanguage, data?.description, translateText]);

  return (
    <>
      <CustomHead title={data?.name} />
      {(dataError || locationError) && (
        <ErrorMessage message={t("fetchError") || locationError} />
      )}

      {!data && !dataError && <Loader m={{ base: "100px", md: "200px" }} />}

      {data?.endOfferDate && <EndProductInfo product={data} />}

      {data && (
        <Box bg="gray.100">
          <Box
            className="main-container"
            px={{ base: "15px", sm: "20px", xl: 0 }}
            mx="auto"
            py={{ base: "15px", sm: "20px", xl: "15px" }}
          >
            <Button
              variant={"link"}
              color="blue.500"
              size="sm"
              leftIcon={<Icon as={ArrowLeft} />}
              onClick={() => router.back()}
              mb="10px"
              display="flex"
              alignItems="center"
              _hover={{
                color: "blue.600",
                textDecoration: "none",
              }}
              _focus={{
                outline: "none",
                color: "blue.600",
              }}
            >
              {t("back")}
            </Button>
            <Flex
              gap="20px"
              p="0px"
              pt={
                dataError || locationError || data?.endOfferDate
                  ? "20px"
                  : undefined
              }
            >
              <Flex
                direction="column"
                gap="20px"
                w={{ base: "100%", lg: "calc(100% - 328px)" }}
              >
                <Flex flexDirection={"column"} w="100%" bg="white" p="25px">
                  <Box className="slider-dark" p={2}>
                    {data?.defaultPhoto ? (
                      <Image
                        fileName={data?.defaultPhoto}
                        alt=""
                        defaultPhoto
                        position="relative"
                        overflow="hidden"
                        _before={{
                          content: '""',
                          pt: "65%",
                          d: "block",
                        }}
                      />
                    ) : (
                      <Slider {...mainSliderSettings}>
                        {data?.photos?.map((photo, index) => (
                          <Image
                            fileName={photo?.filename}
                            alt=""
                            key={photo?.id}
                            position="relative"
                            overflow="hidden"
                            _before={{
                              content: '""',
                              pt: "65%",
                              d: "block",
                            }}
                            _hover={{
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setInitialSlide(index);
                              fullPhotosModal.onOpen();
                            }}
                          />
                        ))}

                        {data?.videoUrl && (
                          <Flex justifyContent={"center"}>
                            <ReactPlayer
                              url={data.videoUrl}
                              width={"100%"}
                              controls={true}
                            />
                          </Flex>
                        )}
                      </Slider>
                    )}
                  </Box>
                  {!data?.endOfferDate && (
                    <HStack
                      spacing={5}
                      align="center"
                      justifyContent={"center"}
                      mt={1}
                    >
                      <Flex
                        _hover={{
                          opacity: 0.7,
                        }}
                        transition="transform 0.3s, opacity 0.3s"
                      >
                        <FacebookShareButton
                          url={`${origin}${
                            origin?.includes("uniqsoft") ? "/gs" : ""
                          }/p/${slug}`}
                        >
                          <FacebookIcon size={40} round={true} />
                        </FacebookShareButton>
                      </Flex>
                      <Flex
                        _hover={{
                          opacity: 0.7,
                        }}
                        transition="transform 0.3s, opacity 0.3s"
                      >
                        <WhatsappShareButton
                          url={`${origin}${
                            origin?.includes("uniqsoft") ? "/gs" : ""
                          }/p/${slug}`}
                        >
                          <WhatsappIcon size={40} round={true} />
                        </WhatsappShareButton>
                      </Flex>
                      <Flex
                        _hover={{
                          opacity: 0.7,
                        }}
                        transition="transform 0.3s, opacity 0.3s"
                      >
                        <EmailShareButton
                          url={`${origin}${
                            origin?.includes("uniqsoft") ? "/gs" : ""
                          }/p/${slug}`}
                        >
                          <EmailIcon size={40} round={true} />
                        </EmailShareButton>
                      </Flex>
                      <Tooltip
                        hasArrow
                        placement="top"
                        label={t("copyPageAddressToClipboard")}
                        bg={"red.500"}
                      >
                        <Flex
                          rounded={"full"}
                          bgColor="red.500"
                          p={"7.5px"}
                          align="center"
                          justifyContent={"center"}
                          cursor="pointer"
                          onClick={() =>
                            toastPromise.promise(
                              navigator.clipboard.writeText(
                                `${origin}${
                                  origin?.includes("uniqsoft") ? "/gs" : ""
                                }/p/${slug}`
                              ),
                              {
                                success: t("copyToClipboardSuccess"),
                                error: t("copyToClipboardError"),
                              }
                            )
                          }
                          _hover={{
                            opacity: 0.7,
                          }}
                          transition="transform 0.3s, opacity 0.3s"
                        >
                          <Icon as={Copy} color="white" w={"25px"} h={"25px"} />
                        </Flex>
                      </Tooltip>
                    </HStack>
                  )}
                </Flex>
                <Box
                  flexDirection={"column"}
                  bg="white"
                  p="25px"
                  display={{ base: "block", lg: "none" }}
                >
                  <Flex justifyContent={"space-between"} align="center">
                    {status === "authenticated" && (
                      <Tooltip
                        hasArrow
                        placement="top"
                        bg={"red.500"}
                        label={
                          data?.productLikes?.length
                            ? t("removeFromFavourites")
                            : t("addToFavourites")
                        }
                      >
                        <Box color="red.500" cursor={"pointer"}>
                          {data?.productLikes?.length ? (
                            <FontAwesomeIcon
                              icon={faHeart}
                              width={32}
                              height={32}
                              color="inherit"
                              onClick={() =>
                                onLikeOrDislikeProduct(
                                  data?.id,
                                  false,
                                  refreshData
                                )
                              }
                            />
                          ) : (
                            <Icon
                              as={Heart}
                              w={8}
                              h={8}
                              color="inherit"
                              onClick={() =>
                                onLikeOrDislikeProduct(
                                  data?.id,
                                  true,
                                  refreshData
                                )
                              }
                            />
                          )}
                        </Box>
                      </Tooltip>
                    )}

                    <Flex flexDir={"column"}>
                      {data?.availabilityDate && !data?.endOfferDate && (
                        <Text
                          textAlign={"end"}
                          color={"gray.600"}
                          fontSize="sm"
                        >
                          {t("product.availabilityDate")}
                          <b>
                            {format(
                              new Date(data?.availabilityDate),
                              "dd.MM.yyyy"
                            )}
                          </b>
                        </Text>
                      )}

                      {data?.expiryDate && !data?.endOfferDate && (
                        <Text
                          textAlign={"end"}
                          color={"gray.600"}
                          fontSize="sm"
                        >
                          {t("product.expiryDate")}
                          <b>
                            {format(new Date(data?.expiryDate), "dd.MM.yyyy")}
                          </b>
                        </Text>
                      )}

                      <Flex gap={1} mt={2}>
                        {!productIsService &&
                          !productIsRealEstate &&
                          !productIsAutomotive && (
                            <Badge colorScheme={data?.isNew ? "red" : "yellow"}>
                              {t(data?.isNew ? "new" : "used")}
                            </Badge>
                          )}
                        <Badge colorScheme={"green"}>
                          {productIsService
                            ? t(
                                data?.shipment
                                  ? "servicesShipmentAllowed"
                                  : "servicesShipmentNotAllowed"
                              )
                            : productIsRealEstate || productIsAutomotive
                            ? `${t("categories.parameter.advertiser")}: ${t(
                                `categories.parameter.options.${productAdvertiserValue}`
                              )}`
                            : t(
                                data?.shipment
                                  ? "shipmentAllowed"
                                  : "shipmentNotAllowed"
                              )}
                        </Badge>
                      </Flex>
                    </Flex>
                  </Flex>

                  <Heading
                    size="md"
                    textTransform="none"
                    color="green.500"
                    letterSpacing={0}
                    mt="10px"
                    mb="10px"
                  >
                    {data?.name}
                  </Heading>

                  <Flex flexDir={"column"} mb={5}>
                    <Heading>
                      {data?.free
                        ? t("products.free")
                        : formatPrice(data?.price, data?.currency)}
                    </Heading>
                    {!data?.free &&
                      parseFloat(data?.lastPrice) &&
                      parseFloat(data?.lastPrice) > parseFloat(data?.price) && (
                        <Text
                          color="GrayText"
                          fontWeight="500"
                          fontSize="xl"
                          textDecoration="line-through"
                          textDecorationColor="red"
                        >
                          {formatPrice(data?.lastPrice, data?.currency)}
                        </Text>
                      )}
                  </Flex>
                  <Buttons
                    product={data}
                    onEndClick={() => {
                      setDialogVariant(DialogType.End);
                      endOrRefreshDialog.onOpen();
                    }}
                    onRefreshClick={() => {
                      setDialogVariant(DialogType.Refresh);
                      endOrRefreshDialog.onOpen();
                    }}
                    onMessageClick={() => {
                      router.push({
                        pathname: `/chat`,
                        query: {
                          receiverId: data?.user.id,
                          productId: data?.id,
                        },
                      });
                    }}
                  />
                </Box>

                {!isUserProduct && (
                  <Box
                    bg="white"
                    p="25px"
                    display={{ base: "block", lg: "none" }}
                  >
                    <GarageInfo
                      garage={data?.garage}
                      productRefresher={refreshData}
                    />
                  </Box>
                )}
                <Box w="100%" bg="white" p="25px">
                  <Box p="10px 15px" bg="gray.100" borderRadius="25px" w="100%">
                    <Flex fontSize="sm" justify="space-between" align="center">
                      {categoryParents?.length > 0 && (
                        <Flex>
                          <Flex gap="5px" flexWrap="wrap">
                            {categoryParents?.map((parent, index) => (
                              <Flex
                                key={`parentCategory_${index}`}
                                alignItems="center"
                              >
                                <Text fontWeight="500">
                                  {t(`categories.${parent?.name}`)}
                                </Text>
                                <Icon
                                  w="20px"
                                  h="20px"
                                  as={ChevronRight}
                                  color="black"
                                  ml="5px"
                                />
                              </Flex>
                            ))}
                            <Text fontWeight="500">
                              {t(`categories.${data?.categories[0]?.name}`)}
                            </Text>
                          </Flex>
                        </Flex>
                      )}
                    </Flex>
                  </Box>

                  {categoryParametersValues?.length > 0 && (
                    <SimpleGrid
                      columns={{ base: 1, sm: 2 }}
                      spacingX="40px"
                      spacingY={1}
                      mt={5}
                    >
                      {categoryParametersValues?.map(
                        (parameterValue, index) => {
                          let value = parameterValue?.value;

                          if (parameterValue?.parameter?.type === "checkbox")
                            value =
                              parameterValue?.value === "true"
                                ? t("yes")
                                : t("no");

                          if (parameterValue?.parameter?.type === "select")
                            value = t(
                              `categories.parameter.options.${parameterValue?.value?.replace(
                                ".",
                                "_"
                              )}`
                            );

                          if (
                            /^\d+$/.test(parameterValue?.value) && //Check if value is number
                            typeof parseFloat(parameterValue?.value) ===
                              "number" &&
                            !isNaN(parseFloat(parameterValue?.value))
                          )
                            value = formatter.format(
                              parseFloat(parameterValue?.value)
                            );

                          return (
                            <Flex
                              key={`parameter_value_${index}`}
                              fontSize="14px"
                              fontWeight="400"
                              justify="space-between"
                              gap="5px"
                            >
                              <chakra.span>
                                {t(
                                  `categories.parameter.${parameterValue?.parameter?.name}`
                                )}
                                :
                              </chakra.span>
                              {parameterValue?.parameter?.name === "vin" ? (
                                <>
                                  {showVin ? (
                                    <chakra.b textAlign="right">
                                      {value}
                                    </chakra.b>
                                  ) : (
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => setShowVin(true)}
                                    >
                                      {t("showVin")}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <chakra.b textAlign="right">{value}</chakra.b>
                              )}
                            </Flex>
                          );
                        }
                      )}
                    </SimpleGrid>
                  )}
                  <Heading size="md" variant="blue" mt="50px">
                    {t("description")}
                  </Heading>
                  {translationLoading && (
                    <Flex justifyContent={"center"}>
                      <Spinner color="red" />
                    </Flex>
                  )}
                  {!translationLoading && !translatedDescription && (
                    <Text align="justify">{data?.description}</Text>
                  )}
                  {translatedDescription && !translationLoading && (
                    <Flex gap={3} flexDir="column">
                      <Text align="justify">{translatedDescription}</Text>
                      <Text fontSize="sm" color="GrayText">
                        {t("autoTranslation")}
                      </Text>
                    </Flex>
                  )}
                </Box>
                <Box bg="white" p="25px">
                  <Buttons
                    product={data}
                    onEndClick={() => {
                      setDialogVariant(DialogType.End);
                      endOrRefreshDialog.onOpen();
                    }}
                    onRefreshClick={() => {
                      setDialogVariant(DialogType.Refresh);
                      endOrRefreshDialog.onOpen();
                    }}
                    onMessageClick={() => {
                      router.push({
                        pathname: `/chat`,
                        query: {
                          receiverId: data?.user.id,
                          productId: data?.id,
                        },
                      });
                    }}
                  />
                </Box>
                {garageProductsData?.count > 0 && (
                  <Box
                    bg="white"
                    p="25px"
                    display={{ base: "block", lg: "none" }}
                  >
                    <Heading size="md" mt={0}>
                      {isUserProduct
                        ? t("product.moreFromMyGarage")
                        : t("product.showMoreFromThisGarage")}
                    </Heading>
                    <Flex
                      direction={{ base: "column", sm: "row" }}
                      gap="20px"
                      flexWrap="wrap"
                    >
                      {garageProductsData?.products?.map((product, index) => (
                        <Box
                          w={{ base: "100%", sm: "calc(50% - 10px)" }}
                          key={index}
                        >
                          <ProductCard product={product} key={index} />
                        </Box>
                      ))}
                      {garageProductsData?.count > GARAGE_PRODUCTS_LIMIT && (
                        <ButtonAnimated
                          text={t("showMore")}
                          size="sm"
                          onClick={() =>
                            router.push(`/g/${data?.garage?.slug}`)
                          }
                        />
                      )}
                    </Flex>
                  </Box>
                )}
              </Flex>
              <Flex
                direction="column"
                gap="20px"
                display={{ base: "none", lg: "flex" }}
                minW="308px"
                w={{ base: "100%", lg: "auto" }}
              >
                <Box flexDirection={"column"} bg="white" p="25px">
                  <Flex justifyContent={"space-between"} align="center">
                    {status === "authenticated" && (
                      <Tooltip
                        hasArrow
                        placement="top"
                        bg={"red.500"}
                        label={
                          data?.productLikes?.length
                            ? t("removeFromFavourites")
                            : t("addToFavourites")
                        }
                      >
                        <Box color="red.500" cursor={"pointer"}>
                          {data?.productLikes?.length ? (
                            <FontAwesomeIcon
                              icon={faHeart}
                              width={32}
                              height={32}
                              color="inherit"
                              onClick={() =>
                                onLikeOrDislikeProduct(
                                  data?.id,
                                  false,
                                  refreshData
                                )
                              }
                            />
                          ) : (
                            <Icon
                              as={Heart}
                              w={8}
                              h={8}
                              color="inherit"
                              onClick={() =>
                                onLikeOrDislikeProduct(
                                  data?.id,
                                  true,
                                  refreshData
                                )
                              }
                            />
                          )}
                        </Box>
                      </Tooltip>
                    )}
                    <Flex flexDir={"column"}>
                      {data?.availabilityDate && !data?.endOfferDate && (
                        <Text
                          textAlign={"end"}
                          color={"gray.600"}
                          fontSize="sm"
                        >
                          {t("product.availabilityDate")}
                          <b>
                            {format(
                              new Date(data?.availabilityDate),
                              "dd.MM.yyyy"
                            )}
                          </b>
                        </Text>
                      )}
                      {data?.expiryDate && !data?.endOfferDate && (
                        <Text
                          textAlign={"end"}
                          color={"gray.600"}
                          fontSize="sm"
                        >
                          {t("product.expiryDate")}
                          <b>
                            {format(new Date(data?.expiryDate), "dd.MM.yyyy")}
                          </b>
                        </Text>
                      )}
                      <Flex gap={1} mt={2}>
                        {!productIsService &&
                          !productIsRealEstate &&
                          !productIsAutomotive && (
                            <Badge colorScheme={data?.isNew ? "red" : "yellow"}>
                              {t(data?.isNew ? "new" : "used")}
                            </Badge>
                          )}
                        <Badge colorScheme={"green"}>
                          {productIsService
                            ? t(
                                data?.shipment
                                  ? "servicesShipmentAllowed"
                                  : "servicesShipmentNotAllowed"
                              )
                            : productIsRealEstate || productIsAutomotive
                            ? `${t("categories.parameter.advertiser")}: ${t(
                                `categories.parameter.options.${productAdvertiserValue}`
                              )}`
                            : t(
                                data?.shipment
                                  ? "shipmentAllowed"
                                  : "shipmentNotAllowed"
                              )}
                        </Badge>
                      </Flex>
                    </Flex>
                  </Flex>

                  <Heading
                    size="md"
                    textTransform="none"
                    color="green.500"
                    letterSpacing={0}
                    mt="10px"
                    mb="10px"
                  >
                    {data?.name}
                  </Heading>

                  <Flex flexDir={"column"} mb={5}>
                    <Heading>
                      {data?.free
                        ? t("products.free")
                        : formatPrice(data?.price, data?.currency)}
                    </Heading>
                    {!data?.free &&
                      parseFloat(data?.lastPrice) &&
                      parseFloat(data?.lastPrice) > parseFloat(data?.price) && (
                        <Text
                          color="GrayText"
                          fontWeight="500"
                          fontSize="xl"
                          textDecoration="line-through"
                          textDecorationColor="red"
                        >
                          {formatPrice(data?.lastPrice, data?.currency)}
                        </Text>
                      )}
                  </Flex>
                  <Buttons
                    product={data}
                    onEndClick={() => {
                      setDialogVariant(DialogType.End);
                      endOrRefreshDialog.onOpen();
                    }}
                    onRefreshClick={() => {
                      setDialogVariant(DialogType.Refresh);
                      endOrRefreshDialog.onOpen();
                    }}
                    onMessageClick={() => {
                      router.push({
                        pathname: `/chat`,
                        query: {
                          receiverId: data?.user.id,
                          productId: data?.id,
                        },
                      });
                    }}
                  />
                </Box>

                {!isUserProduct && (
                  <Box bg="white" p="25px">
                    <GarageInfo
                      garage={data?.garage}
                      productRefresher={refreshData}
                    />
                  </Box>
                )}

                {garageProductsData?.count > 0 && (
                  <Box bg="white" p="25px">
                    <Heading size="md" mt={0}>
                      {isUserProduct
                        ? t("product.moreFromMyGarage")
                        : t("product.showMoreFromThisGarage")}
                    </Heading>
                    <Flex direction="column" gap="20px">
                      {garageProductsData?.products?.map((product, index) => (
                        <ProductCard product={product} key={index} />
                      ))}
                      {garageProductsData?.count > GARAGE_PRODUCTS_LIMIT && (
                        <ButtonAnimated
                          text={t("showMore")}
                          size="sm"
                          onClick={() =>
                            router.push(`/g/${data?.garage?.slug}`)
                          }
                        />
                      )}
                    </Flex>
                  </Box>
                )}
              </Flex>
            </Flex>
          </Box>

          {similarProducts?.length > 0 && (
            <ProductsSlider
              products={similarProducts}
              title={t("product.similarProducts")}
              variant="blue"
              showGarageDistance={location?.lat ? true : false}
            />
          )}

          {fullPhotosModal.isOpen && (
            <FullPhotosModal
              isOpen={fullPhotosModal.isOpen}
              onClose={fullPhotosModal.onClose}
              product={data}
              initialSlide={initialSlide}
              isUserProduct={isUserProduct || data?.endOfferDate}
            />
          )}

          {endOrRefreshDialog.isOpen && (
            <EndOrRefreshDialog
              isOpen={endOrRefreshDialog.isOpen}
              onClose={onCloseDialog}
              onConfirm={onConfirmDialog}
              product={data}
              variant={dialogVariant}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default Product;
